import { getExperimentDetailsOptions } from "@/api/generated/@tanstack/react-query.gen";
import { CopyableId } from "@/components/composite/copyable-id";
import { Page, ResourceError } from "@/components/misc";
import { Protected, ProtectedPage } from "@/components/misc/protected";
import { Breadcrumb, ContentLoader, Tabs } from "@/components/ui";
import { useSubPageProvider } from "@/lib/hooks/use-sub-page";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute, Link, Outlet, useParams } from "@tanstack/react-router";
import {
  ExperimentHeaderActions,
  ExperimentStatusBadge,
} from "./-details-lib/experiment-header-actions";

function ExperimentLayout() {
  const { id } = useParams({ from: "/experiments/$id" });
  const { isSubPage, setIsSubPage, Provider: SubPageProvider } = useSubPageProvider();

  const { data: experiment, isLoading, isError } = useQuery({
    ...getExperimentDetailsOptions({ path: { experiment_id: id } }),
  });

  if (isLoading) {
    return (
      <Page size="full">
        <ContentLoader />
      </Page>
    );
  }

  if (isError) {
    return (
      <Page size="large">
        <ResourceError
          title="Failed to load experiment"
          message="The experiment you're looking for doesn't exist or couldn't be loaded."
        />
      </Page>
    );
  }

  return (
    <Breadcrumb label={experiment?.name ?? id} to={`/experiments/${id}/setup`}>
      <Page size="full">
        {!isSubPage && (
          <Page.Header className="space-y-6 pb-4">
            <Page.TitleContainer className="flex-wrap gap-4">
              <div className="flex flex-col gap-1.5 min-w-0">
                <Page.Title>{experiment?.name}</Page.Title>
                <div className="flex items-center gap-2">
                  <ExperimentStatusBadge status={experiment?.status} />
                  <CopyableId value={id} />
                </div>
              </div>
              <div className="ml-auto">
                <ExperimentHeaderActions experimentId={id} status={experiment?.status} />
              </div>
            </Page.TitleContainer>

            {
              /* Router-nav: each Trigger IS its route Link, which marks itself
                `data-status="active"` (+ aria-current) — so the active tab is
                self-determined. value={null} keeps Base UI from also selecting one. */
            }
            <Tabs variant="line" value={null}>
              <Tabs.List>
                <Protected requires="org:experiment_v2:setup_read">
                  <Tabs.Trigger
                    value="setup"
                    render={
                      <Link
                        to="/experiments/$id/setup"
                        params={{ id }}
                        activeOptions={{ includeSearch: false }}
                      />
                    }
                  >
                    Setup
                  </Tabs.Trigger>
                </Protected>
                <Protected requires="org:experiment_v2:setup_read">
                  <Tabs.Trigger
                    value="variants"
                    render={
                      <Link
                        to="/experiments/$id/variants"
                        params={{ id }}
                        activeOptions={{ includeSearch: false }}
                      />
                    }
                  >
                    Variants
                  </Tabs.Trigger>
                </Protected>
                <Protected requires="org:experiment_v2:setup_read">
                  <Tabs.Trigger
                    value="control"
                    render={
                      <Link
                        to="/experiments/$id/control"
                        params={{ id }}
                        activeOptions={{ includeSearch: false }}
                      />
                    }
                  >
                    Control
                  </Tabs.Trigger>
                </Protected>
                <Protected requires="org:experiment_v2:diagnostics_read">
                  <Tabs.Trigger
                    value="diagnostics"
                    render={
                      <Link
                        to="/experiments/$id/diagnostics"
                        params={{ id }}
                        activeOptions={{ includeSearch: false }}
                      />
                    }
                  >
                    Diagnostics
                  </Tabs.Trigger>
                </Protected>
                {
                  /* Results tab hidden — keeping route + page components in
                    place so the underlying logic can be revived later. */
                }
                <Protected requires="org:experiment_v2:results_read">
                  <Tabs.Trigger
                    value="results"
                    render={
                      <Link
                        to="/experiments/$id/results"
                        params={{ id }}
                        activeOptions={{ includeSearch: false }}
                      />
                    }
                  >
                    Results
                  </Tabs.Trigger>
                </Protected>
              </Tabs.List>
            </Tabs>
          </Page.Header>
        )}

        <SubPageProvider value={setIsSubPage}>
          <Outlet />
        </SubPageProvider>
      </Page>
    </Breadcrumb>
  );
}

export const Route = createFileRoute("/experiments/$id")({
  component: () => (
    <ProtectedPage requires="org:experiment_v2:experiment_v2_read">
      <ExperimentLayout />
    </ProtectedPage>
  ),
});
